import { useOutsideClick } from "hooks";
import { Headings } from "components/ui";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { BarIcon, UserIcon, AngleUpIcon } from "assets/svgs";
import { onToggle, toggleChange } from "store/slices/sidebarSlice";
import { useDispatchSidebarState } from "./useDispatchSidebarState";
import { ConditionalRenderer } from "components/conditional-renderer";
import {
  baseURL,
  licenseValidityStatus,
  userActionTypes,
  userSettingsActionTypes,
} from "utils/constants";
import {
  fetchLoginUserDetail,
  fetchUserSettings,
  logout,
  logoutUser,
  updateUser,
  updateUserSettings,
} from "store/actions";
import {
  companyData,
  userDetailState,
  userSettingState,
} from "store/selectors";
import { UserModal, LicenseExpiryWarning } from "components";
import { removeEmptyKeys } from "utils/helpers";
import { LogoutPopover } from "./logoutPopover";
import styles from "./styles.module.css";

const { invalid } = licenseValidityStatus;
const { updateUserFulfilled } = userActionTypes;
const { updateUserSettingsFulfilled } = userSettingsActionTypes;

export function AppHeader() {
  const dispatch = useAppDispatch();
  const {
    sidebarCollapsedState,
    sidebarToggleCurrentState,
    isSmallScreenState,
  } = useDispatchSidebarState();
  const { isDarkMode, settings, loading } = useAppSelector(userSettingState);
  const { userDetail, userProfile, profileLoading } =
    useAppSelector(userDetailState);
  const { company } = useAppSelector(companyData);
  const [showPopover, setShowPopover] = useState<any>(false);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const ref = useOutsideClick(() => {
    setShowPopover(false);
  });

  useEffect(() => {
    if (company?.license_validity_status === invalid) {
      handleLogout();
    }
  }, [company]);

  async function updateUserHandler(userData: any) {
    setShowUserModal(false);

    const action = await dispatch(
      updateUser({
        userId: userData?.id,
        updatedUser: removeEmptyKeys(userData),
      }),
    );

    if (action.type === updateUserFulfilled) {
      dispatch(fetchLoginUserDetail({ userId: userData?.id }));
    }
  }

  function handleCollapseSidebar() {
    dispatch(toggleChange(!sidebarCollapsedState));
  }

  function handleToggleSidebar() {
    dispatch(onToggle(!sidebarToggleCurrentState));
  }

  async function handleLogout() {
    setShowPopover(false);

    const action = await dispatch(logoutUser({ ...userDetail }));
    if (action.payload.success) {
      localStorage.removeItem("user-detail");
      dispatch(logout());
      dispatch({ type: "LOGOUT" });
    }
  }

  async function themeHandler() {
    const updatedTheme = isDarkMode
      ? {
          dark_theme: false,
          light_theme: true,
        }
      : {
          dark_theme: true,
          light_theme: false,
        };
    const action = await dispatch(
      updateUserSettings({
        updatedSettings: {
          ...(settings[0] || {}),
          ...updatedTheme,
        },
        settingId: settings[0]?.id,
        variant: "theme",
      }),
    );

    if (action.type === updateUserSettingsFulfilled) {
      dispatch(fetchUserSettings({ userId: userDetail?.id }));
    }
  }

  const headerContainerClass = isDarkMode
    ? styles?.appHeaderDarkMode
    : styles.appHeader;

  const profilePicUrl = !userProfile?.profile_pic
    ? ""
    : `${baseURL}${userProfile?.profile_pic}`;
  return (
    <>
      <LicenseExpiryWarning />
      <div className={headerContainerClass}>
        <button
          type="button"
          className={`${styles.barButton}`}
          onClick={
            isSmallScreenState ? handleToggleSidebar : handleCollapseSidebar
          }
        >
          <BarIcon />
        </button>

        <button
          onClick={themeHandler}
          className={`${styles.toggleSwitchBtn} ${
            isDarkMode ? "bg-cardBg" : ""
          }`}
        >
          <div
            className={`${styles.toggleSwitchCircle} ${
              isDarkMode ? "translate-x-8 bg-navyTeal" : "translate-x-0"
            }`}
          >
            <span className="text-xs">{isDarkMode ? "☀️" : "🌙"}</span>
          </div>
        </button>

        <div className={styles.userData} ref={ref}>
          <button
            type="button"
            className={`${styles.userAvatarButton} ${
              showPopover
                ? isDarkMode
                  ? "bg-darkSecondary"
                  : "bg-cyan-50"
                : ""
            }`}
            onClick={() => setShowPopover(!showPopover)}
          >
            {!profileLoading && profilePicUrl ? (
              <img
                src={profilePicUrl}
                className={`${styles.avatarImage}`}
                alt="user profile"
              />
            ) : (
              <UserIcon className={styles.avatar} />
            )}
            <Headings.LevelFour
              classes={
                isDarkMode ? "text-secondary mb-0" : "text-lightGreen mb-0"
              }
            >
              {userDetail?.username || ""}
            </Headings.LevelFour>
            <AngleUpIcon
              className={`${styles.angle} ${showPopover ? styles.rotate : ""}`}
            />
          </button>
          <ConditionalRenderer condition={showPopover}>
            <LogoutPopover
              onLogoutClick={handleLogout}
              onProfileSettingClick={() => setShowUserModal(true)}
            />
          </ConditionalRenderer>
        </div>
      </div>

      <ConditionalRenderer condition={showUserModal}>
        <UserModal
          showModal={showUserModal}
          onClose={() => setShowUserModal(false)}
          onSubmit={updateUserHandler}
        />
      </ConditionalRenderer>
    </>
  );
}
